import React, { useState, useMemo } from "react";
import { useTranslation } from "react-i18next";

import { IWorkspaceUserExtended, WorkspaceUserRole } from "common/interfaces";
import { delimiterRegex, emailRegex } from "common/regex";

const useUpgradeToTeamForm = ({ users = [], workspaceName }: { users?: IWorkspaceUserExtended[]; workspaceName: string;}) => {
    const { t } = useTranslation();
    const [teamName, setTeamName] = useState<string>(workspaceName);
    const [teamMembers, setTeamMembers] = useState<string[]>([]);
    const [emailError, setEmailError] = useState<string>("");
    const [isEmailInputFocused, setIsEmailInputFocused] = useState<boolean>(false);

    const existingUserEmails = useMemo(
        () =>
            (users ?? [])
                .map(u => u?.email?.toLowerCase?.())
                .filter(Boolean) as string[],
        [users]
    );

    const ownerEmails = useMemo(
        () =>
            (users ?? [])
                .filter(u => u.role === WorkspaceUserRole.OWNER)
                .map(u => u?.email?.toLowerCase?.())
                .filter(Boolean) as string[],
        [users]
    );

    const [selectedExisting, setSelectedExisting] = useState<string[]>(
        () => existingUserEmails.filter(e => !ownerEmails.includes(e))
    );

    const validateEmail = (email: string): boolean => emailRegex.test(email.trim());

    const isDuplicateEmail = (email: string, allEmails: string[]): boolean => {
        const normalizedEmail = email.toLowerCase().trim();
        return allEmails.filter(e => e.toLowerCase().trim() === normalizedEmail).length > 1;
    };

    const workspaceUsersToInvite = () => {
        const newInvites = teamMembers
            .filter(email => !!email)
            .map(email => ({
                email,
                role: WorkspaceUserRole.MEMBER,
            }));
        return [...newInvites];
    };

    const handlePaste = (event: React.ClipboardEvent) => {
        event.persist();

        const text = event.clipboardData?.getData("text") || "";
        if (text) {
            event.stopPropagation();
            event.preventDefault();

            const emails = text.split(delimiterRegex).map(d => d.trim()).filter(Boolean);
            if (emails.length > 0) {
                // Add the pasted emails to the current tags
                const currentTags = [...ownerEmails, ...selectedExisting, ...teamMembers];
                const newTags = [...currentTags, ...emails];
                handleTagsChange(newTags);
            }
        }
    };

    const handleTagsChange = (tags: any[]) => {
        const normalized = tags
            .map(t => (typeof t === "object" ? (t.email?.toLowerCase?.() || "") : String(t).toLowerCase()))
            .map(s => s.trim())
            .filter(Boolean);

        // Split current tags into existing (excluding owners) and new
        const nextSelectedExisting = normalized.filter(
            e => existingUserEmails.includes(e) && !ownerEmails.includes(e)
        );
        const nextNew = normalized.filter(e => !existingUserEmails.includes(e));

        setSelectedExisting(nextSelectedExisting);
        setTeamMembers(nextNew);

        // Check for invalid emails and duplicates
        const allCurrentEmails = [...ownerEmails, ...nextSelectedExisting, ...nextNew];
        const invalids = nextNew.filter(e => !validateEmail(e));
        const duplicates = allCurrentEmails.filter(e => isDuplicateEmail(e, allCurrentEmails));

        const errorMessages = [];
        if (invalids.length > 0) {
            const label = invalids.length === 1 ? t("Invalid email") : t("Invalid emails");
            errorMessages.push(`${label}: ${invalids.join(", ")}`);
        }
        if (duplicates.length > 0) {
            const label = duplicates.length === 1 ? t("Duplicate email") : t("Duplicate emails");
            errorMessages.push(`${label}: ${duplicates.join(", ")}`);
        }

        setEmailError(errorMessages.length ? errorMessages.join("; ") : "");
    };

    return {
        teamName,
        setTeamName,
        ownerEmails,
        selectedExisting,
        teamMembers,
        emailError,
        isEmailInputFocused,
        setIsEmailInputFocused,
        validateEmail,
        isDuplicateEmail,
        handleTagsChange,
        handlePaste,
        workspaceUsersToInvite,
    };
};

export default useUpgradeToTeamForm;
